import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import config from '../config';

const ALLOWED_TOP_LEVEL_KEYS = new Set([
  'status',
  'service',
  'environment',
  'timestamp',
  'checks',
]);

const ALLOWED_CHECK_KEYS = new Set([
  'name',
  'status',
  'critical',
  'message',
  'latency_ms',
]);

function statusChipColor(status) {
  switch (status) {
    case 'ok':
      return 'success';
    case 'degraded':
      return 'warning';
    case 'unavailable':
      return 'error';
    default:
      return 'default';
  }
}

function redactHealthPayload(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const safe = {};
  ALLOWED_TOP_LEVEL_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(raw, key)) {
      safe[key] = raw[key];
    }
  });
  if (Array.isArray(safe.checks)) {
    safe.checks = safe.checks.map((row) => {
      const check = {};
      ALLOWED_CHECK_KEYS.forEach((key) => {
        if (row && Object.prototype.hasOwnProperty.call(row, key)) {
          check[key] = row[key];
        }
      });
      return check;
    });
  }
  return safe;
}

export default function HealthPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${config.API_BASE_URL}/health`);
        const json = await response.json().catch(() => null);
        const redactedPayload = redactHealthPayload(json);
        if (!cancelled) {
          setPayload(redactedPayload);
          if (!response.ok) {
            setError(`Health request returned ${response.status}`);
          }
        }
        if (!response.ok && !redactedPayload) {
          throw new Error(`Health request failed (${response.status})`);
        }
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(err.message || 'Failed to load health status');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayStatus = useMemo(() => {
    if (loading) {
      return 'loading';
    }
    if (payload?.status) {
      return payload.status;
    }
    return 'unavailable';
  }, [loading, payload]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4" component="h1">
            API Health
          </Typography>
          <Chip
            label={displayStatus}
            color={statusChipColor(displayStatus)}
            data-testid="health-status-chip"
          />
        </Stack>

        {loading && (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size="1.5rem" />
            <Typography variant="body1">Loading health status…</Typography>
          </Stack>
        )}

        {error && !loading && (
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        )}

        {payload && !loading && (
          <>
            <Typography variant="body2" color="text.secondary">
              Backend timestamp: {payload.timestamp || '—'}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {(payload.checks || []).map((check) => (
                <Box component="li" key={check.name} sx={{ mb: 1 }}>
                  <Typography variant="body1">
                    {check.name}: {check.status}
                    {check.message ? ` — ${check.message}` : ''}
                    {typeof check.latency_ms === 'number'
                      ? ` (${check.latency_ms} ms)`
                      : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Stack>
    </Container>
  );
}
